import React, { useState, useEffect, useMemo } from "react";
import {
  EditButton,
  List,
  ShowButton,
  useDataGrid,
  DeleteButton,
} from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  BaseRecord,
  getDefaultFilter,
  HttpError,
  IResourceComponentsProps,
  useTranslate,
  useList,
} from "@refinedev/core";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { format } from "date-fns";

export interface IPromocode {
  id: number;
  code: string;
  duration: number;
  maxUses: number;
  usedCount: number;
  expiresAt: Date;
  isActive: boolean;
  usedBy: any[];
}

export interface IPromocodeFilterVariables {
  q?: string;
  code?: string;
  isActive?: boolean;
}

export const PromocodeList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const theme = useTheme();

  // Add state for clipboard notification
  const [copySnackbar, setCopySnackbar] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  // Function to handle copy to clipboard
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(
      () => {
        setCopySnackbar({
          open: true,
          message: `Code "${code}" copied to clipboard!`,
        });
      },
      (err) => {
        console.error("Could not copy text: ", err);
        setCopySnackbar({
          open: true,
          message: "Failed to copy code.",
        });
      }
    );
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setCopySnackbar({
      ...copySnackbar,
      open: false,
    });
  };

  // Ajout d'un état de chargement pour le filtrage
  const [isFiltering, setIsFiltering] = useState(false);
  // État pour stocker tous les éléments et éléments filtrés
  const [filteredItems, setFilteredItems] = useState<IPromocode[]>([]);
  // État pour stocker les anciennes lignes (pour éviter le flash de toutes les lignes)
  const [lastValidRows, setLastValidRows] = useState<IPromocode[]>([]);
  // État pour les critères de filtrage actifs - AJOUT DE CETTE LIGNE MANQUANTE
  const [activeFilters, setActiveFilters] = useState<IPromocodeFilterVariables>(
    {}
  );

  // Récupération de tous les promocodes avec useList au lieu de refetch
  const { data: promocodesData } = useList<IPromocode>({
    resource: "promo-codes",
  });

  const allItems = useMemo(() => promocodesData?.data || [], [promocodesData]);

  const { dataGridProps, search, filters } = useDataGrid<
    IPromocode,
    HttpError,
    IPromocodeFilterVariables
  >({
    initialPageSize: 10,
    onSearch: (params) => {
      // Activer l'état de filtrage
      setIsFiltering(true);
      // Stockez les filtres actuels
      setActiveFilters(params);

      // Sauvegarder l'état actuel des lignes
      if (dataGridProps.rows && dataGridProps.rows.length > 0) {
        setLastValidRows(dataGridProps.rows as IPromocode[]);
      }

      // Filtrage côté client, retourner un tableau vide pour les filtres API
      return [];
    },
    pagination: { mode: "server" },
  });

  // État pour la pagination locale
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Effet pour filtrer les éléments selon les critères
  useEffect(() => {
    if (!allItems.length) return;

    // Fonction pour filtrer les éléments selon les critères actifs
    const performFiltering = async () => {
      let result = [...allItems];
      const { code, isActive } = activeFilters;

      // Filtre par code s'il est spécifié
      if (code && code !== "") {
        result = result.filter((item) =>
          item.code.toLowerCase().includes(code.toLowerCase())
        );
      }

      // Filtre par statut actif s'il est spécifié
      if (isActive !== undefined && isActive !== null) {
        result = result.filter((item) => item.isActive === Boolean(isActive));
      }

      // Utiliser setTimeout pour éviter le flash des résultats
      setTimeout(() => {
        setFilteredItems(result);
        setIsFiltering(false);
      }, 50);
    };

    performFiltering();
  }, [activeFilters, allItems]);

  // Effet pour mettre à jour lastValidRows séparément
  useEffect(() => {
    if (!isFiltering && dataGridProps.rows && dataGridProps.rows.length > 0) {
      setLastValidRows(dataGridProps.rows as IPromocode[]);
    }
  }, [dataGridProps.rows, isFiltering]);

  // Override des lignes du DataGrid avec nos données filtrées mais en préservant la pagination
  const getFilteredRows = () => {
    // Pendant le filtrage, afficher les dernières lignes valides pour éviter un flash
    if (isFiltering && lastValidRows.length > 0) {
      return lastValidRows;
    }

    // Si aucun filtre actif, utiliser les données originales du dataGridProps pour préserver pagination
    if (
      (!activeFilters.code || activeFilters.code === "") &&
      (activeFilters.isActive === undefined || activeFilters.isActive === null)
    ) {
      return dataGridProps.rows || [];
    }

    // Si filtrage terminé, appliquer la pagination manuellement aux résultats
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return filteredItems.slice(start, end);
  };

  const columns = React.useMemo<GridColDef<IPromocode>[]>(
    () => [
      {
        field: "isActive",
        headerName: "",
        width: 40,
        minWidth: 40,
        flex: 0,
        filterable: false,
        sortable: false,

        renderCell: function render({ value }) {
          return (
            <Tooltip
              title={
                value
                  ? translate("isActive.active", "Active")
                  : translate("isActive.inactive", "Inactive")
              }
              arrow
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                  cursor: "help",
                }}
              >
                {value ? "🟢" : "🔴"}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "code",
        flex: 1,
        headerName: translate("promoCode.fields.code", "Code"),
        minWidth: 150,
        renderCell: function render({ value }) {
          const isDark = theme.palette.mode === 'dark';
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: "1.1rem",
                  letterSpacing: "0.5px",
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  backgroundColor: isDark ? theme.palette.grey[700] : "#e0e0e0",
                  color: isDark ? theme.palette.common.white : theme.palette.common.black,
                  whiteSpace: "nowrap",
                  cursor: "text",
                }}
              >
                {value}
              </Box>
              <Tooltip title="Copy code">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row selection
                    handleCopyCode(value);
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "duration",
        flex: 0,
        headerName: translate("promoCode.fields.duration", "Duration (days)"),
        type: "number",
        width: 80,
        minWidth: 80,
        renderCell: function render({ value }) {
          return (
            <Box>
              {value} {translate("promoCode.fields.durationUnit", "days")}
            </Box>
          );
        },
      },
      {
        field: "usage",
        flex: 1,
        headerName: translate("promoCode.fields.usage", "Usage"),
        minWidth: 100,
        renderCell: function render({ row }) {
          return (
            <Box>
              {row.usedCount}/{row.maxUses}
            </Box>
          );
        },
      },
      {
        field: "expiresAt",
        flex: 1,
        headerName: translate("promoCode.fields.expiresAt", "Expires At"),
        minWidth: 120,
        renderCell: function render({ value }) {
          try {
            return value ? format(new Date(value), "dd/MM/yyyy") : "";
          } catch (error) {
            console.error("Error formatting date:", error, value);
            return "";
          }
        },
      },
      {
        field: "actions",
        headerName: translate("promoCode.fields.actions", "Actions"),
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteButton hideText recordItemId={row.id} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 120,
      },
    ],
    [translate]
  );

  const { register, handleSubmit, control, reset } = useForm<
    BaseRecord,
    HttpError,
    IPromocodeFilterVariables
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq") || "",
      code: getDefaultFilter("code", filters, "contains") || "",
      isActive: getDefaultFilter("isActive", filters, "eq"),
    },
  });

  // Add reset function to clear filters
  const handleClearFilters = () => {
    reset({
      q: "",
      code: "",
      isActive: undefined,
    });
    search({ q: "", code: "", isActive: undefined });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Card>
            <CardHeader title={translate("pages.filter", "Filters")} />
            <CardContent sx={{ pt: 0 }}>
              <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column" }}
                autoComplete="off"
                onSubmit={handleSubmit(search)}
              >
                <TextField
                  {...register("code")}
                  label={translate("pages.search", "Search")}
                  placeholder={translate(
                    "pages.searchByCode",
                    "Search by code"
                  )}
                  margin="normal"
                  fullWidth
                  autoFocus
                  size="small"
                />
                <Controller
                  control={control}
                  name="isActive"
                  render={({ field }) => (
                    <Autocomplete
                      options={[true, false, null]}
                      {...field}
                      onChange={(_, value) => {
                        field.onChange(value);
                      }}
                      getOptionLabel={(item) => {
                        if (item === true) return "Active";
                        if (item === false) return "Inactive";
                        return "All";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("isActive.title", "Active Status")}
                          placeholder={translate(
                            "isActive.select",
                            "Select status"
                          )}
                          margin="normal"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button type="submit" variant="contained">
                    {translate("buttons.search", "Search")}
                  </Button>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    {translate("buttons.clear", "Clear")}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={9}>
          <List>
            <DataGrid
              {...dataGridProps}
              rows={getFilteredRows()}
              rowCount={
                // Si filtrage actif, utiliser le nombre total d'éléments filtrés
                (!activeFilters.code || activeFilters.code === "") &&
                (activeFilters.isActive === undefined ||
                  activeFilters.isActive === null)
                  ? dataGridProps.rowCount
                  : filteredItems.length
              }
              columns={columns}
              autoHeight
              loading={isFiltering} // Afficher un indicateur de chargement pendant le filtrage
              disableRowSelectionOnClick
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10]}
              paginationMode="server"
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
            />
          </List>
        </Grid>
      </Grid>
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
